import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './App.jsx';
import { useAuthState } from './context/AuthContext';

// דפים
import {
  LandingInfo,
  VideoGate,
  EmployeeLogin,
  QuizPlayer,
  Results,
  AdminLogin,
  AdminCourseList,
  AdminCourseEditor,
  UserLogin,
  UserRegister,
  UserDashboard,
  AdminUsersList,
  PasswordReset,
  AdminExamsList,
  ExamEditor,
  ExamPlayer,
  ExamResults,
  EmailVerification,
  SurveyPage,
  AdminSurveyAnalytics,
  RegulationsPage,
  AdminRegulationsList,
  AdminRegulationEditor,
  AdminRegulationStats,
  ProceduresPage,
  AdminProceduresList,
  AdminProcedureEditor,
} from './pages';

import AdminDashboard from './pages/Admin/Dashboard/index.jsx';
import SectorManagerDashboard from '../src/pages/SectorManager/Dashboard/index.jsx';

// Layout לאדמין
import AdminLayout from './components/AdminLayout';

// לודרים/אקשנים
import { loader as dashboardLoader } from './pages/UserDashboard/index.jsx';
import { action as adminLoginAction } from './pages/Admin/Login.jsx';

// מסך טעינה
const LoadingScreen = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: 'var(--bg)',
      color: 'var(--text)',
    }}
  >
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>טוען...</div>
      <div style={{ color: 'var(--text)' }}>מאמת פרטי התחברות</div>
    </div>
  </div>
);

// ראוט מוגן - עם תמיכה בתפקידים
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isLoggedIn, user, loading } = useAuthState();

  if (loading) return <LoadingScreen />;
  if (!isLoggedIn) return <Navigate to='/user/login' replace />;

  // אם יש הגבלת תפקידים, בדוק אותה
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // הפנה לפי התפקיד
    if (user?.role === 'sector_manager') {
      return <Navigate to='/manager/dashboard' replace />;
    }
    return <Navigate to='/user/dashboard' replace />;
  }

  return children;
};

// ראוט ציבורי
const PublicRoute = ({
  children,
  redirectTo = '/user/dashboard',
  isAdminRoute = false,
}) => {
  const { isLoggedIn, loading, user } = useAuthState();
  if (loading) return <LoadingScreen />;

  if (isLoggedIn) {
    // הפנה לפי תפקיד
    if (user?.role === 'admin' || user?.role === 'org_manager') {
      return <Navigate to='/admin' replace />;
    }
    if (user?.role === 'sector_manager') {
      return <Navigate to='/manager/dashboard' replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// רכיב שגיאות
const RouteError = () => {
  return (
    <div style={{ padding: 24, color: 'var(--text)' }}>
      אירעה שגיאה בטעינת הדף
    </div>
  );
};

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <RouteError />,
      children: [
        { index: true, element: <Navigate to='/user/login' replace /> },

        // ========================================
        // ציבורי - משתמשים
        // ========================================
        {
          path: 'user/login',
          element: (
            <PublicRoute>
              <UserLogin />
            </PublicRoute>
          ),
        },
        {
          path: 'user/register',
          element: (
            <PublicRoute>
              <UserRegister />
            </PublicRoute>
          ),
        },
        {
          path: 'user/verify-email',
          element: (
            <PublicRoute>
              <EmailVerification />
            </PublicRoute>
          ),
        },
        {
          path: 'reset-password',
          element: (
            <PublicRoute>
              <PasswordReset />
            </PublicRoute>
          ),
        },

        // ========================================
        // דשבורד משתמש רגיל
        // ========================================
        {
          path: 'user/dashboard',
          element: (
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          ),
          loader: dashboardLoader,
          errorElement: <RouteError />,
        },
        {
          path: 'survey',
          element: (
            <ProtectedRoute>
              <SurveyPage />
            </ProtectedRoute>
          ),
          errorElement: <RouteError />,
        },
        {
          path: 'regulations',
          element: (
            <ProtectedRoute>
              <RegulationsPage />
            </ProtectedRoute>
          ),
          errorElement: <RouteError />,
        },
        {
          path: 'procedures',
          element: (
            <ProtectedRoute>
              <ProceduresPage />
            </ProtectedRoute>
          ),
          errorElement: <RouteError />,
        },
        // ========================================
        // מבחנים (לכל המשתמשים המחוברים)
        // ========================================
        {
          path: 'exams/:slug',
          element: (
            <ProtectedRoute>
              <ExamPlayer />
            </ProtectedRoute>
          ),
        },
        {
          path: 'exams/:slug/results',
          element: (
            <ProtectedRoute>
              <ExamResults />
            </ProtectedRoute>
          ),
        },

        // ========================================
        // התחברות אדמין (ציבורי)
        // ========================================
        {
          path: 'admin/login',
          element: (
            <PublicRoute isAdminRoute={true}>
              <AdminLogin />
            </PublicRoute>
          ),
          action: adminLoginAction,
        },

        // ========================================
        // אזור אדמין + מנהל ארגון
        // ========================================
        {
          path: 'admin',
          element: (
            <ProtectedRoute allowedRoles={['admin', 'org_manager']}>
              <AdminLayout />
            </ProtectedRoute>
          ),
          children: [
            // דשבורד ראשי
            {
              index: true,
              element: <AdminDashboard />,
            },
            // ניהול לומדות
            {
              path: 'courses',
              element: <AdminCourseList />,
            },

            {
              path: 'courses/:slug',
              element: <AdminCourseEditor />,
            },
            // ניהול מבחנים
            {
              path: 'exams',
              element: <AdminExamsList />,
            },
            {
              path: 'exams/:slug',
              element: <ExamEditor />,
            },
            // ניהול משתמשים
            {
              path: 'users',
              element: <AdminUsersList />,
            },
            // סקרי אקלים
            {
              path: 'surveys',
              element: <AdminSurveyAnalytics />,
            },
            // תקנונים
            {
              path: 'regulations',
              element: <AdminRegulationsList />,
            },
            {
              path: 'regulations/:id',
              element: <AdminRegulationEditor />,
            },
            {
              path: 'regulations/:id/stats',
              element: <AdminRegulationStats />,
            },
            // נהלי עבודה
            {
              path: 'procedures',
              element: <AdminProceduresList />,
            },
            {
              path: 'procedures/:id',
              element: <AdminProcedureEditor />,
            },
            // דוחות
            {
              path: 'reports',
              element: (
                <div className='admin-page'>
                  <div className='page-header'>
                    <h1 className='page-title'>
                      <span className='page-title-icon'>📈</span>
                      דוחות
                    </h1>
                  </div>
                  <div className='admin-card'>
                    <p
                      style={{
                        color: 'var(--admin-text-muted)',
                        textAlign: 'center',
                        padding: '40px',
                      }}
                    >
                      מודול הדוחות בפיתוח...
                    </p>
                  </div>
                </div>
              ),
            },
            // הגדרות (אדמין בלבד)
            {
              path: 'settings',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <div className='admin-page'>
                    <div className='page-header'>
                      <h1 className='page-title'>
                        <span className='page-title-icon'>⚙️</span>
                        הגדרות
                      </h1>
                    </div>
                    <div className='admin-card'>
                      <p
                        style={{
                          color: 'var(--admin-text-muted)',
                          textAlign: 'center',
                          padding: '40px',
                        }}
                      >
                        מודול ההגדרות בפיתוח...
                      </p>
                    </div>
                  </div>
                </ProtectedRoute>
              ),
            },
          ],
        },

        // ========================================
        // אזור מנהל קטגוריה
        // ========================================
        {
          path: 'manager',
          children: [
            {
              path: 'dashboard',
              element: (
                <ProtectedRoute
                  allowedRoles={['sector_manager', 'admin', 'org_manager']}
                >
                  <SectorManagerDashboard />
                </ProtectedRoute>
              ),
            },
          ],
        },

        // ========================================
        // מסלולי קורסים למשתמשים
        // ========================================
        {
          path: ':slug',
          element: (
            <ProtectedRoute>
              <LandingInfo />
            </ProtectedRoute>
          ),
        },
        {
          path: ':slug/video',
          element: (
            <ProtectedRoute>
              <VideoGate />
            </ProtectedRoute>
          ),
        },
        {
          path: ':slug/quiz',
          element: (
            <ProtectedRoute>
              <QuizPlayer />
            </ProtectedRoute>
          ),
        },
        {
          path: ':slug/results',
          element: (
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          ),
        },

        // תאימות לאחור
        { path: ':slug/login', element: <Navigate to='/user/login' replace /> },

        // 404
        { path: '*', element: <Navigate to='/user/login' replace /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
  }
);
